import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { PATH } from '../const'
import { AssetAdmin } from '../modules/asset-admin'
import {
  AccountLocked,
  ConfirmAccount,
  Login,
  NotFound,
  PermissionsRequired,
  ResetPassword,
  ResetPasswordRequest,
  ResetPasswordSent,
  TwoFactor,
  TwoFactorNotSetUp
} from '../modules/authentication/pages'
import { Logout } from '../modules/authentication/pages/logout'
import { BankUsers } from '../modules/bank-users'
import { CashInstrumentAdmin } from '../modules/cash-instrument-admin'
import { DraftTrades } from '../modules/draft-trades'
import { FundsReinvestmentPage } from '../modules/funds-backoffice'
import GapRiskPage from '../modules/gap-risk'
import { PortfolioAdmin } from '../modules/model-portfolio-admin'
import { NoteApprovalsPage } from '../modules/note-approvals/pages'
import { OrdersWrapper } from '../modules/orders-backoffice/orders-wrapper/orders-wrapper'
import { ReconciliationWrapper } from '../modules/reconciliation-backoffice/reconciliation-wrapper/reconciliation-wrapper'
import { ReportingNotes } from '../modules/reporting/pages/notes'
import { ReportingOverview } from '../modules/reporting/pages/overview'
import { ReportingTrade } from '../modules/reporting/pages/trade'
import { ReportingTradesList } from '../modules/reporting/pages/trades-list'
import { StrategyAdmin } from '../modules/strategy-admin'
import { TaxReports } from '../modules/tax-report'
import { TradeClose } from '../modules/trade-close/trade-close-layout'
import TradeEditor from '../modules/trade-editor'
import { TradeBuilder, TradeBuilderExistingAccount } from '../modules/tradebuilder-v2'
import { UnitTransfersWrapper } from '../modules/unit-transfers-backoffice/unit-transfers-wrapper/unit-transfers-wrapper'
import { ClientsLayout } from '../modules/user-admin/clients-layout-provider'
import { ChangePasswordPage } from '../modules/user-settings/change-password'
import { Home } from './Home'
import { ReportingWrapper } from './Reporting'
import { RequireAuth } from './RequireAuth'

export const Router = () => (
  <BrowserRouter
    future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }}
  >
    <Routes>
      <Route element={<Home />} path={PATH.home} />
      <Route element={<Login />} path={PATH.login} />
      <Route element={<Logout />} path={PATH.logout} />
      <Route element={<TwoFactor />} path={PATH.twoFactor} />
      <Route element={<ResetPasswordRequest />} path={PATH.resetPasswordRequest} />
      <Route element={<ResetPasswordSent />} path={PATH.resetPasswordSent} />
      <Route element={<ResetPassword />} path={PATH.resetPassword} />
      <Route element={<PermissionsRequired />} path={PATH.permissionsRequired} />
      <Route element={<ConfirmAccount />} path={PATH.confirmAccount} />
      <Route element={<AccountLocked inactive />} path={PATH.accountInactive} />
      <Route element={<AccountLocked />} path={PATH.accountLocked} />
      <Route element={<TwoFactorNotSetUp />} path={PATH.twoFactorNotSetUp} />
      <Route
        element={
          <RequireAuth permissionRequired="settings.change_password">
            <ChangePasswordPage />
          </RequireAuth>
        }
        path={PATH.changePassword}
      />
      <Route element={<ReportingWrapper />} path="/reports/*">
        <Route element={<Navigate to={PATH.reportsOverview} />} path="" />
        <Route element={<ReportingOverview />} path="overview" />
        <Route element={<ReportingNotes />} path="notes" />
        <Route element={<ReportingNotes />} path="notes/:noteId/:noteTab" />
        <Route element={<ReportingTradesList />} path="trades" />
        <Route element={<ReportingTrade />} path="trades/:source/:tradeId/*" />
      </Route>
      <Route element={<NoteApprovalsPage />} path="note-approvals/*" />
      <Route
        element={
          <RequireAuth permissionRequired="user.business_unit.view">
            <ClientsLayout />
          </RequireAuth>
        }
        path={PATH.usersClients}
      />
      <Route
        element={
          <RequireAuth permissionRequired="organisation.portfolio_manager.view">
            <ClientsLayout isPortfolioManagerScreen />
          </RequireAuth>
        }
        path={PATH.portfolioManagers}
      />
      <Route
        element={
          <RequireAuth permissionRequired="organisation.bank.view">
            <BankUsers />
          </RequireAuth>
        }
        path={PATH.usersAbsa}
      />
      <Route
        element={
          <RequireAuth permissionRequired="product_admin.assets.view">
            <AssetAdmin />
          </RequireAuth>
        }
        path={PATH.productAdminAssets}
      />
      <Route
        element={
          <RequireAuth permissionRequired="product_admin.portfolios.view">
            <PortfolioAdmin />
          </RequireAuth>
        }
        path={PATH.productAdminPortfolios}
      />

      <Route
        element={
          <RequireAuth permissionRequired="product_admin.strategies.view">
            <StrategyAdmin />
          </RequireAuth>
        }
        path={PATH.productAdminStrategies}
      />

      <Route
        element={
          <RequireAuth permissionRequired="product_admin.deposit.view">
            <CashInstrumentAdmin pageType="Deposit" />
          </RequireAuth>
        }
        path={PATH.productAdminDepositInstruments}
      />

      <Route
        element={
          <RequireAuth permissionRequired="product_admin.loan.view">
            <CashInstrumentAdmin pageType="Loan" />
          </RequireAuth>
        }
        path={PATH.productAdminLoanInstruments}
      />

      <Route
        element={
          <RequireAuth
            permissionRequired="trade.view"
            roles={[
              'Administrator',
              'BankAdmin',
              'BusinessUnitUser',
              'Editor',
              'Client',
              'FLStructuring',
              'Operations',
              'Trading',
              'Sales'
            ]}
          >
            <DraftTrades />
          </RequireAuth>
        }
        path={PATH.trades}
      />

      <Route
        element={
          <RequireAuth permissionRequired="risk.view">
            <GapRiskPage />
          </RequireAuth>
        }
        path={PATH.gapRisk}
      />

      <Route
        element={
          <RequireAuth
            permissionRequired="trade.view"
            roles={['Administrator', 'BankAdmin', 'Editor', 'FLStructuring', 'Sales']}
          >
            <TradeBuilder />
          </RequireAuth>
        }
        path={PATH.tradeBuilderNew}
      />

      <Route
        element={
          <RequireAuth
            permissionRequired="trade.view"
            roles={[
              'Administrator',
              'BankAdmin',
              'BusinessUnitUser',
              'Editor',
              'FLStructuring',
              'Sales'
            ]}
          >
            <TradeBuilderExistingAccount />
          </RequireAuth>
        }
        path={PATH.tradeBuilderExisting(':accountId')}
      />

      <Route
        element={
          <RequireAuth
            permissionRequired="trade.view"
            roles={[
              'Administrator',
              'BankAdmin',
              'BusinessUnitUser',
              'Editor',
              'FLStructuring',
              'Sales'
            ]}
          >
            <TradeEditor />
          </RequireAuth>
        }
        path={PATH.tradeBuilderEdit(':accountId')}
      />

      <Route
        element={
          <RequireAuth
            permissionRequired="trade.view"
            roles={[
              'Administrator',
              'BankAdmin',
              'BusinessUnitUser',
              'Editor',
              'FLStructuring',
              'Sales'
            ]}
          >
            <TradeClose />
          </RequireAuth>
        }
        path={PATH.tradeBuilderClose(':accountId')}
      />

      <Route
        element={
          <RequireAuth
            permissionRequired="trade.view"
            roles={[
              'Administrator',
              'BankAdmin',
              'BusinessUnitUser',
              'Editor',
              'FLStructuring',
              'Sales'
            ]}
          >
            <TradeClose closeAll />
          </RequireAuth>
        }
        path={PATH.tradeBuilderCloseAll(':accountId')}
      />

      <Route
        element={
          <RequireAuth permissionRequired="backoffice.view">
            <FundsReinvestmentPage />
          </RequireAuth>
        }
        path={PATH.backOfficeFunds}
      />

      <Route
        element={
          <RequireAuth permissionRequired="backoffice.view">
            <TaxReports />
          </RequireAuth>
        }
        path={PATH.backOfficeTaxReports}
      />

      <Route
        element={
          <RequireAuth permissionRequired="backoffice.view">
            <OrdersWrapper />
          </RequireAuth>
        }
        path={PATH.backOfficeOrders}
      />

      <Route
        element={
          <RequireAuth permissionRequired="backoffice.view">
            <ReconciliationWrapper />
          </RequireAuth>
        }
        path={PATH.backOfficeReconciliation}
      />

      <Route
        element={
          <RequireAuth permissionRequired="backoffice.view">
            <UnitTransfersWrapper />
          </RequireAuth>
        }
        path={PATH.backOfficeUnitTransfers}
      />

      <Route element={<NotFound />} path="*" />
    </Routes>
  </BrowserRouter>
)
